/**
 * CRM API client: plain functions, no React imports.
 * Base URL comes from REACT_APP_API_PATH, the token from the existing session (x-token).
 * The company always comes from the token, so CompanyId is never sent.
 * Failures throw a CrmRequestError ({ status, data }) -- run them through toApiError() at the call site.
 */
import axios from 'axios'
import { APIPath } from 'utility/constant/api'
import { StorageConstant } from 'utility/constant/common'
import { BrowserUtility } from 'utility/browser-utility'

/** @typedef {import('utility/crm/types').CrmLookups} CrmLookups */
/** @typedef {import('utility/crm/types').CrmSummary} CrmSummary */
/** @typedef {import('utility/crm/types').CrmSearchHit} CrmSearchHit */
/** @typedef {import('utility/crm/types').CrmAccount} CrmAccount */
/** @typedef {import('utility/crm/types').CrmAccountInput} CrmAccountInput */
/** @typedef {import('utility/crm/types').CrmContact} CrmContact */
/** @typedef {import('utility/crm/types').CrmContactInput} CrmContactInput */
/** @typedef {import('utility/crm/types').CrmContactsQuery} CrmContactsQuery */
/** @typedef {import('utility/crm/types').CrmContactsPage} CrmContactsPage */
/** @typedef {import('utility/crm/types').CrmBulkInput} CrmBulkInput */
/** @typedef {import('utility/crm/types').CrmImportInput} CrmImportInput */
/** @typedef {import('utility/crm/types').CrmImportResult} CrmImportResult */
/** @typedef {import('utility/crm/types').CrmDeal} CrmDeal */
/** @typedef {import('utility/crm/types').CrmDealInput} CrmDealInput */
/** @typedef {import('utility/crm/types').CrmDealsQuery} CrmDealsQuery */
/** @typedef {import('utility/crm/types').CrmForecast} CrmForecast */
/** @typedef {import('utility/crm/types').CrmQuoteInput} CrmQuoteInput */
/** @typedef {import('utility/crm/types').CrmProposal} CrmProposal */
/** @typedef {import('utility/crm/types').CrmWonInput} CrmWonInput */
/** @typedef {import('utility/crm/types').CrmWonResult} CrmWonResult */
/** @typedef {import('utility/crm/types').CrmActivity} CrmActivity */
/** @typedef {import('utility/crm/types').CrmActivitiesQuery} CrmActivitiesQuery */
/** @typedef {import('utility/crm/types').CrmActivityInput} CrmActivityInput */
/** @typedef {import('utility/crm/types').CrmFollowups} CrmFollowups */
/** @typedef {import('utility/crm/types').CrmTask} CrmTask */
/** @typedef {import('utility/crm/types').CrmTaskInput} CrmTaskInput */
/** @typedef {import('utility/crm/types').CrmSavedView} CrmSavedView */

/** Thrown by every call: carries the HTTP status (0 = no response) and the parsed body. */
export class CrmRequestError extends Error {
    constructor(status, data) {
        super(`CRM request failed (${status})`)
        this.name = 'CrmRequestError'
        this.status = status
        this.data = data
    }
}

let unauthorizedHandler = null
let accessDeniedHandler = null

/**
 * Register what happens on a 401 from a CRM call (the shell passes the auth logout).
 * @param {(() => void)|null} handler
 */
export const setCrmUnauthorizedHandler = handler => {
    unauthorizedHandler = handler
}

/**
 * Register what happens when any CRM call answers 403 CRM_NOT_ALLOWED (the shell switches to a "no access" screen).
 * @param {(() => void)|null} handler
 */
export const setCrmAccessDeniedHandler = handler => {
    accessDeniedHandler = handler
}

/**
 * Drop empty values so they are not sent as "param=".
 * @param {Record<string, unknown>} [params]
 */
export const buildQuery = params => {
    const search = new URLSearchParams()
    Object.entries(params || {}).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '' || value === false) return
        search.append(key, String(value))
    })
    const text = search.toString()
    return text ? `?${text}` : ''
}

/**
 * @template T
 * @param {'get'|'post'|'put'|'delete'} method
 * @param {string} path relative to the API base, e.g. "CRM/Contacts"
 * @param {{ params?: Record<string, unknown>, body?: unknown }} [options]
 * @returns {Promise<T>}
 */
const call = async (method, path, { params, body } = {}) => {
    try {
        const response = await axios({
            method,
            url: `${APIPath.server}${path}${buildQuery(params)}`,
            data: body,
            headers: { 'x-token': BrowserUtility.get(StorageConstant.token) || '' },
        })
        return response.data
    } catch (error) {
        const status = error?.response?.status || 0
        if (status === 401 && unauthorizedHandler) unauthorizedHandler()
        if (status === 403 && error?.response?.data?.ErrorCode === 'CRM_NOT_ALLOWED' && accessDeniedHandler) accessDeniedHandler()
        throw new CrmRequestError(status, error?.response?.data)
    }
}

const enc = id => encodeURIComponent(String(id))

// ---- Lookups, summary, search -------------------------------------------------

/** @returns {Promise<CrmLookups>} */
export const getLookups = () => call('get', 'CRM/Lookups')

/** @returns {Promise<CrmSummary>} */
export const getSummary = () => call('get', 'CRM/Summary')

/** @param {string} q min 2 characters @returns {Promise<CrmSearchHit[]>} */
export const search = q => call('get', 'CRM/Search', { params: { q } })

// ---- Accounts ----------------------------------------------------------------

/**
 * @param {{search?: string, type?: string, ownerUserId?: number, sort?: 'risk'|'name'}} [query]
 * @returns {Promise<CrmAccount[]>}
 */
export const getAccounts = query => call('get', 'CRM/Accounts', { params: query })

/**
 * @param {number} id
 * @returns {Promise<{Account: CrmAccount, Contacts: CrmContact[], Deals: CrmDeal[],
 *   Inspections: {AuditId: number, Date: string, TemplateName: string, Score: number|null, Status: string}[],
 *   Tickets: {TicketId: number, Title: string, StatusId: number, DueOn: string|null, IsOpen: boolean, IsOverdue: boolean}[]}>}
 */
export const getAccount = id => call('get', `CRM/Accounts/${enc(id)}`)

/** @param {CrmAccountInput} input @returns {Promise<CrmAccount>} */
export const createAccount = input => call('post', 'CRM/Accounts', { body: input })

/** @param {number} id @param {CrmAccountInput} input @returns {Promise<CrmAccount>} */
export const updateAccount = (id, input) => call('put', `CRM/Accounts/${enc(id)}`, { body: input })

/** Admin only. @param {number} id */
export const deleteAccount = id => call('delete', `CRM/Accounts/${enc(id)}`)

// ---- Contacts ----------------------------------------------------------------

/** @param {CrmContactsQuery} [query] @returns {Promise<CrmContactsPage>} */
export const getContacts = query => call('get', 'CRM/Contacts', { params: query })

/** @param {number} id @returns {Promise<CrmContact>} */
export const getContact = id => call('get', `CRM/Contacts/${enc(id)}`)

/** @param {CrmContactInput} input @returns {Promise<CrmContact>} */
export const createContact = input => call('post', 'CRM/Contacts', { body: input })

/** @param {number} id @param {CrmContactInput} input @returns {Promise<CrmContact>} */
export const updateContact = (id, input) => call('put', `CRM/Contacts/${enc(id)}`, { body: input })

/** Admin only. @param {number} id */
export const deleteContact = id => call('delete', `CRM/Contacts/${enc(id)}`)

/** @param {CrmBulkInput} input max 500 ids @returns {Promise<{Updated: number}>} */
export const bulkUpdateContacts = input => call('post', 'CRM/Contacts/Bulk', { body: input })

/** Dry run, nothing saved. @param {CrmImportInput} input @returns {Promise<CrmImportResult>} */
export const previewContactImport = input => call('post', 'CRM/Contacts/Import/Preview', { body: input })

/** @param {CrmImportInput} input @returns {Promise<CrmImportResult>} */
export const importContacts = input => call('post', 'CRM/Contacts/Import', { body: input })

// ---- Deals -------------------------------------------------------------------

/** @param {CrmDealsQuery} [query] @returns {Promise<CrmDeal[]>} */
export const getDeals = query => call('get', 'CRM/Deals', { params: query })

/** @param {string} [pipeline] @returns {Promise<CrmForecast>} */
export const getForecast = pipeline => call('get', 'CRM/Deals/Forecast', { params: { pipeline } })

/** @param {number} id @returns {Promise<CrmDeal>} */
export const getDeal = id => call('get', `CRM/Deals/${enc(id)}`)

/** @param {CrmDealInput} input @returns {Promise<CrmDeal>} */
export const createDeal = input => call('post', 'CRM/Deals', { body: input })

/** 409 DEAL_WON for won deals. @param {number} id @param {CrmDealInput} input @returns {Promise<CrmDeal>} */
export const updateDeal = (id, input) => call('put', `CRM/Deals/${enc(id)}`, { body: input })

/** Admin only. @param {number} id */
export const deleteDeal = id => call('delete', `CRM/Deals/${enc(id)}`)

/** "Won" is rejected with 400 USE_WON_ENDPOINT -- use markDealWon. @param {number} id @param {string} stage @returns {Promise<CrmDeal>} */
export const moveDealStage = (id, stage) => call('post', `CRM/Deals/${enc(id)}/Stage`, { body: { Stage: stage } })

/** @param {number} id @param {string} reason @returns {Promise<CrmDeal>} */
export const markDealLost = (id, reason) => call('post', `CRM/Deals/${enc(id)}/Lost`, { body: { Reason: reason } })

/** @param {number} id @returns {Promise<CrmDeal>} */
export const reopenDeal = id => call('post', `CRM/Deals/${enc(id)}/Reopen`)

/** Live price, nothing saved. @param {CrmQuoteInput} input @returns {Promise<CrmProposal>} */
export const quoteProposal = input => call('post', 'CRM/Proposal/Quote', { body: input })

/**
 * Saves the proposal, sets Amount = AnnualValue and moves the deal to Proposal Sent.
 * @param {number} id
 * @param {CrmQuoteInput} input
 * @returns {Promise<{Quote: CrmProposal, Deal: CrmDeal}>}
 */
export const saveDealProposal = (id, input) =>
    call('post', `CRM/Deals/${enc(id)}/Proposal`, { body: { ...input, Save: true } })

/** @param {number} id @param {CrmWonInput} input @returns {Promise<CrmWonResult>} */
export const markDealWon = (id, input) => call('post', `CRM/Deals/${enc(id)}/Won`, { body: input })

// ---- Activities --------------------------------------------------------------

/** @param {CrmActivitiesQuery} query one of contactId / accountId / dealId @returns {Promise<CrmActivity[]>} */
export const getActivities = query => call('get', 'CRM/Activities', { params: query })

/** @param {CrmActivityInput} input @returns {Promise<CrmActivity>} */
export const createActivity = input => call('post', 'CRM/Activities', { body: input })

// ---- Tasks / follow-ups ------------------------------------------------------

/** @param {boolean} mine @returns {Promise<CrmFollowups>} */
export const getFollowups = mine => call('get', 'CRM/Followups', { params: { mine: mine ? 'true' : 'false' } })

/**
 * @param {{contactId?: number, accountId?: number, includeDone?: boolean}} [query]
 * @returns {Promise<CrmTask[]>}
 */
export const getTasks = query => call('get', 'CRM/Tasks', { params: query })

/** @param {CrmTaskInput} input @returns {Promise<CrmTask>} */
export const createTask = input => call('post', 'CRM/Tasks', { body: input })

/** @param {number} id @param {CrmTaskInput} input @returns {Promise<CrmTask>} */
export const updateTask = (id, input) => call('put', `CRM/Tasks/${enc(id)}`, { body: input })

/** @param {number} id @returns {Promise<{Task: CrmTask, NextRecurring: CrmTask|null}>} */
export const completeTask = id => call('post', `CRM/Tasks/${enc(id)}/Complete`)

/** @param {number} id @returns {Promise<CrmTask>} */
export const reopenTask = id => call('post', `CRM/Tasks/${enc(id)}/Reopen`)

/** @param {number} id @param {number} days 1-90 @returns {Promise<CrmTask>} */
export const snoozeTask = (id, days) => call('post', `CRM/Tasks/${enc(id)}/Snooze`, { body: { Days: days } })

/** @param {number} id */
export const deleteTask = id => call('delete', `CRM/Tasks/${enc(id)}`)

// ---- Saved views -------------------------------------------------------------

/** @returns {Promise<CrmSavedView[]>} */
export const getViews = () => call('get', 'CRM/Views')

/** @param {{Name: string, Filters: CrmContactsQuery}} input @returns {Promise<CrmSavedView>} */
export const createView = input => call('post', 'CRM/Views', { body: input })

/** @param {number} id */
export const deleteView = id => call('delete', `CRM/Views/${enc(id)}`)
